import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { Department } from 'src/app/shared/models/lawyers/department';
import { DepartmentSearchResults } from 'src/app/shared/models/lawyers/departmentSearchResults';
import { Lawyer } from 'src/app/shared/models/lawyers/lawyer';
import { LawyerLevel } from 'src/app/shared/models/lawyers/lawyerLevel';
import { LawyerLevelSearchResults } from 'src/app/shared/models/lawyers/lawyerLevelSearchResults';
import { LawyerSearchResults, LawyerSearchResultsLite } from 'src/app/shared/models/lawyers/lawyerSearchResults';
import { SearchParams } from 'src/app/shared/models/searchParams';
import { environment } from 'src/environments/environment';
import { ErrorService } from '../error/error.service';
import { ApiBaseService } from './api-base.service';

@Injectable({
  providedIn: 'root'
})
export class LawyersApiService extends ApiBaseService {

  baseUrl = environment.backend_host + '/api/lawyers';

  constructor(
    public http: HttpClient,
    protected errorService: ErrorService,
    protected spinner: NgxSpinnerService) {
    super(spinner, errorService);
  }

  // user this with detailed list
  public getLawyers = (params: SearchParams): Observable<LawyerSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/`, { params: p })
      .pipe(map(this.deserializeAs(LawyerSearchResults)));
  }

  public getLawyersWithoutUsers = (params: SearchParams): Observable<LawyerSearchResultsLite> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/without-users`, { params: p })
      .pipe(map(this.deserializeAs(LawyerSearchResultsLite)));
  }

  // use this with listing lawyers in tables
  public getLawyersLite = (params: SearchParams): Observable<LawyerSearchResultsLite> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/lite`, { params: p })
      .pipe(map(this.deserializeAs(LawyerSearchResultsLite)));
  }

  public getLawyer = (id: number): Observable<Lawyer> => {
    return this.http.get<any>(`${this.baseUrl}/${id}`)
      .pipe(map(this.deserializeAs(Lawyer)));
  }

  public updateLawyer = (lawyer: Lawyer): Observable<Lawyer> => {
    return this.http.put<any>(`${this.baseUrl}/${lawyer.id}`, this.serialize(lawyer))
      .pipe(map(this.deserializeAs(Lawyer)));
  }

  public createLawyer = (lawyer: Lawyer): Observable<Lawyer> => {
    return this.http.post<any>(`${this.baseUrl}/`, this.serialize(lawyer))
      .pipe(map(this.deserializeAs(Lawyer)));
  }

  public deleteLawyer = (lawyer: Lawyer): Observable<Lawyer> => {
    return this.http.delete<any>(`${this.baseUrl}/${lawyer.id}`)
      .pipe(share());
  }

  public getLawyerContributions = (id: number): Observable<any> => {
    return this.http.get<any>(`${this.baseUrl}/${id}/contributions`);
  }

  // Departments
  public getDepartments = (params: SearchParams): Observable<DepartmentSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/departments`, { params: p })
      .pipe(map(this.deserializeAs(DepartmentSearchResults)));
  }

  public getDepartment = (id: number): Observable<Department> => {
    return this.http.get<any>(`${this.baseUrl}/departments/${id}`)
      .pipe(map(this.deserializeAs(Department)));
  }

  public updateDepartment = (department: Department): Observable<Department> => {
    return this.http.put<any>(`${this.baseUrl}/departments/${department.id}`, this.serialize(department))
      .pipe(map(this.deserializeAs(Department)));
  }

  public createDepartment = (department: Department): Observable<Department> => {
    return this.http.post<any>(`${this.baseUrl}/departments`, this.serialize(department))
      .pipe(map(this.deserializeAs(Department)));
  }

  public deleteDepartment = (department: Department): Observable<Department> => {
    return this.http.delete<any>(`${this.baseUrl}/departments/${department.id}`)
      .pipe(share());
  }

  // Lawyer Positions/Levels
  public getLawyerLevels = (params: SearchParams): Observable<LawyerLevelSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/positions`, { params: p })
      .pipe(map(this.deserializeAs(LawyerLevelSearchResults)));
  }

  public getLawyerLevel = (id: number): Observable<LawyerLevel> => {
    return this.http.get<any>(`${this.baseUrl}/positions/${id}`)
      .pipe(map(this.deserializeAs(LawyerLevel)));
  }

  public updateLawyerLevel = (position: LawyerLevel): Observable<LawyerLevel> => {
    return this.http.put<any>(`${this.baseUrl}/positions/${position.id}`, this.serialize(position))
      .pipe(map(this.deserializeAs(LawyerLevel)));
  }

  public createLawyerLevel = (position: LawyerLevel): Observable<LawyerLevel> => {
    return this.http.post<any>(`${this.baseUrl}/positions`, this.serialize(position))
      .pipe(map(this.deserializeAs(LawyerLevel)));
  }

  public deleteLawyerLevel = (position: LawyerLevel): Observable<LawyerLevel> => {
    return this.http.delete<any>(`${this.baseUrl}/positions/${position.id}`)
      .pipe(share());
  }

}
